import React, { useState, useEffect, useCallback, useRef, useId, useMemo } from 'react';
import { useSSE } from '../hooks/useSSE';
import { tablesAPI, tableCategoriesAPI, ordersAPI, menuAPI, paymentsAPI, promoCodesAPI, giftVouchersAPI, waiterCallAPI, settingsAPI, customersAPI, reservationsAPI } from '../services/api';
import { printReceipt } from '../utils/printReceipt';
import { SkeletonList } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../context/ConfirmContext';
import useFocusTrap from '../hooks/useFocusTrap';
import useBackButtonClose from '../hooks/useBackButtonClose';

const CAT_COLORS = [
  '#6b7280','#3b82f6','#8b5cf6','#10b981',
  '#f97316','#ef4444','#f59e0b','#ec4899',
  '#14b8a6','#0ea5e9','#84cc16','#a855f7',
];

/* ─── BottomSheet ──────────────────────────────────────────────── */
function BottomSheet({ title, subtitle, onClose, children, wide }) {
  const { t } = useLanguage();
  const panelRef = useRef(null);
  const titleId = useId();
  useFocusTrap(true, panelRef, { onEscape: onClose });
  useBackButtonClose(true, onClose);
  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div ref={panelRef} className={`relative bg-white dark:bg-gray-900 rounded-t-3xl md:rounded-2xl shadow-2xl w-full ${wide === 'checkout' ? 'md:max-w-5xl' : wide === 'order' ? 'md:max-w-4xl' : wide ? 'md:max-w-2xl' : 'md:max-w-xl'} max-h-[92vh] md:max-h-[88vh] flex flex-col`}>
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} aria-label={t('close')} className="w-11 h-11 md:w-9 md:h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 active:scale-95 transition-transform">✕</button>
        </div>
        {wide === 'order'
          ? <div className="flex-1 flex overflow-hidden">{children}</div>
          : <div className="overflow-y-auto app-scroll flex-1 px-5 py-4">{children}</div>
        }
      </div>
    </div>
  );
}

function BigButton({ onClick, color, children, disabled, small }) {
  const colors = {
    blue:   'bg-blue-600 active:bg-blue-800 text-white',
    green:  'bg-green-600 active:bg-green-800 text-white',
    yellow: 'bg-amber-500 active:bg-amber-700 text-white',
    red:    'bg-red-600 active:bg-red-800 text-white',
    gray:   'bg-gray-100 dark:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`w-full rounded-2xl font-semibold transition-all active:scale-[0.98] disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${small ? 'py-3 text-sm' : 'py-4 text-base'} ${colors[color] || colors.blue}`}>
      {children}
    </button>
  );
}

const STATUS_STYLE = {
  preparing: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500 animate-pulse' },
  ready:     { bg: 'bg-green-100 dark:bg-green-900/40',  text: 'text-green-700 dark:text-green-300',  dot: 'bg-green-500' },
  served:    { bg: 'bg-blue-100 dark:bg-blue-900/40',    text: 'text-blue-700 dark:text-blue-300',    dot: 'bg-blue-500' },
};

/* ─── Manage Popover ───────────────────────────────────────────── */
function ManagePopover({ table, onClose, onCreateOrder, onReset, onDelete }) {
  const { t } = useLanguage();
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [onClose]);

  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-40 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 min-w-[180px] md:min-w-[160px]">
      <button onClick={onCreateOrder}
        className="w-full flex items-center gap-2 px-4 py-3 md:py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 transition-colors">
        {t('addOrder')}
      </button>
      {table.status === 'occupied' && (
        <button onClick={onReset}
          className="w-full flex items-center gap-2 px-4 py-3 md:py-2.5 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 active:bg-amber-100 dark:active:bg-amber-900/40 transition-colors">
          {t('closeTable')}
        </button>
      )}
      <div className="h-px bg-gray-100 dark:bg-gray-700 mx-2 my-1" />
      <button onClick={onDelete}
        className="w-full flex items-center gap-2 px-4 py-3 md:py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 active:bg-red-100 dark:active:bg-red-900/40 transition-colors">
        {t('deleteTableAction')}
      </button>
    </div>
  );
}

/* ─── Main Component ───────────────────────────────────────────── */
export default function Tables() {
  const { t, lang } = useLanguage();
  const confirm = useConfirm();
  const statusMap = {
    preparing: { ...STATUS_STYLE.preparing, label: t('orderPreparing') },
    ready:     { ...STATUS_STYLE.ready,     label: t('orderReady') },
    served:    { ...STATUS_STYLE.served,    label: t('orderServedShort') },
  };
  const [tables, setTables]               = useState([]);
  const [tableCategories, setTableCategories] = useState([]);
  // Bugünkü aktif rezervasyonu olan masaların id Set'i — backend tables.status'u 'reserved'a
  // güncellemediği için reservations join ile masa kartını amber göstermek için kullanılıyor.
  const [reservedTableIds, setReservedTableIds] = useState(() => new Set());
  const [loading, setLoading]             = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [sheet, setSheet]                 = useState(null);
  const [activeOrders, setActiveOrders]   = useState([]);
  const [menuItems, setMenuItems]         = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuFilter, setMenuFilter]       = useState('all');
  const [newOrder, setNewOrder]           = useState({ items: [], notes: '' });
  const [newTable, setNewTable]           = useState({ name: '', capacity: 4, table_category_id: '' });
  const [bulkMode, setBulkMode]           = useState(false);
  const [bulkTable, setBulkTable]         = useState({ prefix: t('tableBulkPrefixDefault') || 'Masa', count: 5, startFrom: 1, capacity: 4, table_category_id: '' });
  const [checkoutType, setCheckoutType]   = useState('cash');
  const [splitCash, setSplitCash]         = useState('');
  const [splitCard, setSplitCard]         = useState('');
  const [discount, setDiscount]           = useState({ mode: 'amount', value: '' });
  const [promoCode, setPromoCode]         = useState('');
  const [promoResult, setPromoResult]     = useState(null); // { valid, promo, discount_amount }
  const [promoLoading, setPromoLoading]   = useState(false);
  const [voucherCode, setVoucherCode]     = useState('');
  const [voucherResult, setVoucherResult] = useState(null); // { valid, voucher }
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [, setShowDiscountExtras] = useState(false);
  // Müşteri seçici (sadakat / yaşam boyu değer takibi için opsiyonel)
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch]               = useState('');
  // Filtre/sıralama tercihleri localStorage'da hatırlanır — sayfa değiştirip dönünce eski seçim kalır.
  const [statusFilter, setStatusFilter]   = useState(() => {
    try {
      const v = localStorage.getItem('tables_status_filter');
      return ['all', 'occupied', 'free', 'reserved'].includes(v) ? v : 'all';
    } catch { return 'all'; }
  });
  const [categoryFilter, setCategoryFilter] = useState(() => {
    try {
      const v = localStorage.getItem('tables_category_filter');
      if (v === null || v === 'all') return 'all';
      const n = Number(v);
      return Number.isFinite(n) ? n : 'all';
    } catch { return 'all'; }
  });
  const [sortKey, setSortKey] = useState(() => {
    try {
      const v = localStorage.getItem('tables_sort_key');
      return ['name', 'capacity', 'status', 'duration'].includes(v) ? v : 'name';
    } catch { return 'name'; }
  });
  const [sortDir] = useState('asc'); // reserved for future direction toggle
  const [openPopover, setOpenPopover]     = useState(null);
  const [error, setError]                 = useState('');
  const [submitting, setSubmitting]       = useState(false);
  const [restaurantName, setRestaurantName] = useState('');

  // Category management
  const [catForm, setCatForm]   = useState({ name: '', color: '#6b7280' });
  const [editCat, setEditCat]   = useState(null);
  const [catSaving, setCatSaving] = useState(false);

  // Tur 270: kategori chip drag-drop reorder (localStorage persisted)
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [categoryOrder, setCategoryOrder] = useState(() => {
    try {
      const raw = localStorage.getItem('tables_category_order');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(n => Number.isFinite(n)) : [];
    } catch { return []; }
  });

  const sortedTableCategories = useMemo(() => {
    if (!categoryOrder.length) return tableCategories;
    const byId = new Map(tableCategories.map(c => [c.id, c]));
    const ordered = [];
    categoryOrder.forEach(id => { if (byId.has(id)) { ordered.push(byId.get(id)); byId.delete(id); } });
    byId.forEach(c => ordered.push(c)); // remaining (new) categories appended
    return ordered;
  }, [tableCategories, categoryOrder]);

  const persistCategoryOrder = useCallback((ids) => {
    setCategoryOrder(ids);
    try { localStorage.setItem('tables_category_order', JSON.stringify(ids)); } catch { /* quota */ }
  }, []);

  const handleCatDragStart = (idx) => (e) => {
    setDraggedIdx(idx);
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); } catch { /* ignore */ }
  };
  const handleCatDragOver = (idx) => (e) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    setDragOverIdx(idx);
  };
  const handleCatDrop = (idx) => (e) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) { setDraggedIdx(null); setDragOverIdx(null); return; }
    const next = [...sortedTableCategories];
    const [moved] = next.splice(draggedIdx, 1);
    next.splice(idx, 0, moved);
    persistCategoryOrder(next.map(c => c.id));
    setDraggedIdx(null); setDragOverIdx(null);
  };

  // Waiter calls
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [callsExpanded, setCallsExpanded] = useState(true);

  const loadWaiterCalls = useCallback(async () => {
    try {
      const res = await waiterCallAPI.getCalls({ status: 'pending' });
      setWaiterCalls(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const acknowledgeCall = async (id) => {
    try {
      await waiterCallAPI.updateStatus(id, 'acknowledged');
      setWaiterCalls(prev => prev.map(c => c.id === id ? { ...c, status: 'acknowledged' } : c));
    } catch { /* ignore */ }
  };

  const resolveCall = async (id) => {
    try {
      await waiterCallAPI.updateStatus(id, 'resolved');
      setWaiterCalls(prev => prev.filter(c => c.id !== id));
    } catch { /* ignore */ }
  };

  const loadAll = useCallback(async () => {
    try {
      const [tablesRes, catsRes, reservRes] = await Promise.all([
        tablesAPI.getAll(),
        tableCategoriesAPI.getAll(),
        // Bugünkü ve yaklaşan aktif rezervasyonlar — masa kartını amber göstermek için.
        // Hata olursa rezervasyon işareti gösterilmez ama sayfa yüklenmeye devam eder.
        reservationsAPI.getAll({ upcoming: 1 }).catch(() => ({ data: { data: [] } })),
      ]);
      setTables(tablesRes.data.data || []);
      setTableCategories(catsRes.data.data || []);
      // Bugün için reservation_time + duration_minutes hâlâ geleceğe denk gelen rezervasyonların
      // table_id'lerini Set'e topla (1-2 saat sonra olan rezervasyon da göster).
      const reservList = reservRes.data?.data || [];
      const todayStr = new Date().toISOString().slice(0, 10);
      const nowMs = Date.now();
      const ids = new Set();
      for (const r of reservList) {
        if (!r?.table_id) continue;
        if (r.reservation_date !== todayStr) continue; // sadece bugün
        // reservation_time "HH:MM" veya "HH:MM:SS" — bitiş zamanı = start + duration_minutes
        const [hh, mm] = String(r.reservation_time || '00:00').split(':').map(Number);
        const start = new Date(`${todayStr}T${String(hh).padStart(2,'0')}:${String(mm||0).padStart(2,'0')}:00`);
        const dur = Number(r.duration_minutes) || 90;
        const end = new Date(start.getTime() + dur * 60 * 1000);
        // Şu an rezervasyonun başlangıcından max 4 saat ÖNCE veya rezervasyonun bitiş zamanından önce
        const fourHoursBeforeMs = start.getTime() - 4 * 60 * 60 * 1000;
        if (nowMs >= fourHoursBeforeMs && nowMs < end.getTime()) {
          ids.add(r.table_id);
        }
      }
      setReservedTableIds(ids);
    } catch { setError(t('tablesLoadError')); }
    finally { setLoading(false); }
  }, [t]);

  // Filtre/sıralama tercihlerini değiştikçe kaydet.
  useEffect(() => {
    try { localStorage.setItem('tables_status_filter', statusFilter); } catch { /* quota */ }
  }, [statusFilter]);
  useEffect(() => {
    try { localStorage.setItem('tables_category_filter', String(categoryFilter)); } catch { /* quota */ }
  }, [categoryFilter]);
  useEffect(() => {
    try { localStorage.setItem('tables_sort_key', sortKey); } catch { /* quota */ }
  }, [sortKey]);

  // Hidrasyon sigortası: useState lazy init herhangi bir nedenle başarısız olduysa,
  // mount sonrası localStorage'dan tekrar oku. Bu mount-sonrası storage event veya
  // race condition'lara karşı defansif bir fallback (kullanıcı şikayeti: chip persist
  // bazen sayfa değişince kayboluyor). Sadece mount'ta çalışmalı.
  useEffect(() => {
    try {
      const cf = localStorage.getItem('tables_category_filter');
      if (cf !== null && cf !== 'all') {
        const n = Number(cf);
        if (Number.isFinite(n)) setCategoryFilter(prev => prev !== n ? n : prev);
      }
      const sf = localStorage.getItem('tables_status_filter');
      if (sf && ['all', 'occupied', 'free', 'reserved'].includes(sf)) {
        setStatusFilter(prev => prev !== sf ? sf : prev);
      }
      const sk = localStorage.getItem('tables_sort_key');
      if (sk && ['name', 'capacity', 'status', 'duration'].includes(sk)) {
        setSortKey(prev => prev !== sk ? sk : prev);
      }
    } catch { /* localStorage erişim engeli */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kategori sanity: kategoriler yüklendikten sonra stored filter o id'ye sahip değilse 'all'a düş.
  useEffect(() => {
    if (!tableCategories.length || categoryFilter === 'all') return;
    if (!tableCategories.some(c => c.id === categoryFilter)) {
      setCategoryFilter('all');
    }
  }, [tableCategories, categoryFilter]);

  useEffect(() => {
    loadAll();
    loadWaiterCalls();
    // Fiş başlığında göstermek için restoran adını yükle (bir kere yeter)
    settingsAPI.get()
      .then(res => setRestaurantName(res.data?.data?.name || ''))
      .catch(() => {});
    // SSE yokken yedek polling (60s — SSE anında günceller)
    const interval = setInterval(loadAll, 60000);
    return () => clearInterval(interval);
  }, [loadAll, loadWaiterCalls]);

  // SSE: sipariş/ödeme/garson çağrısı değişince anlık yenile
  useSSE(({ type }) => {
    if (type === 'tables' || type === 'orders' || type === 'all') loadAll();
    if (type === 'waiter_call' || type === 'all') loadWaiterCalls();
  });

  /* Stat overview: tablo durum & kapasite özeti */
  const tableStats = useMemo(() => {
    let occupied = 0, empty = 0, reserved = 0, capacity = 0;
    for (const tbl of tables) {
      capacity += Number(tbl.capacity) || 0;
      if (tbl.status === 'occupied') occupied += 1;
      else if (tbl.status === 'reserved') reserved += 1;
      else empty += 1; // 'empty' veya diğer her şey boş sayılır
    }
    return { occupied, empty, reserved, capacity };
  }, [tables]);

  /* Filtered + sorted tables */
  const displayedTables = useMemo(() => {
    const filtered = tables.filter(tbl => {
      const matchSearch   = tbl.name.toLowerCase().includes(search.toLowerCase());
      const matchStatus   = statusFilter === 'all'
        || tbl.status === statusFilter
        // Reserved filter: backend status YA DA frontend reservation join'i. occupied önceliği var.
        || (statusFilter === 'reserved' && tbl.status !== 'occupied' && reservedTableIds.has(tbl.id));
      const matchCategory = categoryFilter === 'all' || tbl.table_category_id === categoryFilter;
      return matchSearch && matchStatus && matchCategory;
    });
    const statusPriority = { occupied: 1, free: 2, reserved: 3 };
    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'capacity') return (a.capacity || 0) - (b.capacity || 0);
      if (sortKey === 'status') {
        return (statusPriority[a.status] || 9) - (statusPriority[b.status] || 9);
      }
      if (sortKey === 'duration') {
        const ta = a.oldest_pending_at ? new Date(a.oldest_pending_at).getTime() : Infinity;
        const tb = b.oldest_pending_at ? new Date(b.oldest_pending_at).getTime() : Infinity;
        return ta - tb; // oldest (smaller timestamp) first
      }
      return (a.name || '').localeCompare(b.name || '', 'tr');
    });
    return sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [tables, search, statusFilter, categoryFilter, sortKey, sortDir, reservedTableIds]);
  const filteredTables = displayedTables;

  /* ── Order actions ── */
  const loadActiveOrders = async (table) => {
    const res = await tablesAPI.getOrders(table.id);
    const data = res.data.data;
    setActiveOrders(Array.isArray(data) ? data : (data?.orders || []));
  };

  const handleTableClick = async (table) => {
    setOpenPopover(null);
    setSelectedTable(table);
    if (table.status === 'occupied') {
      try {
        await loadActiveOrders(table);
        setSheet('active-orders');
      } catch { setError(t('ordersLoadError')); }
    } else {
      openCreateOrder(table);
    }
  };

  const openCreateOrder = async (table) => {
    const tbl = table || selectedTable;
    setSelectedTable(tbl);
    try {
      const [itemsRes, catsRes] = await Promise.all([menuAPI.getItems({ available: true }), menuAPI.getCategories()]);
      setMenuItems(itemsRes.data.data || []);
      setMenuCategories(catsRes.data.data || []);
      setNewOrder({ items: [], notes: '' });
      setMenuFilter('all');
      setSheet('create-order');
    } catch { setError(t('menuLoadError')); }
  };

  const toggleItem = (item) => {
    setNewOrder(prev => {
      const exists = prev.items.find(i => i.menu_item_id === item.id);
      if (exists) return { ...prev, items: prev.items.filter(i => i.menu_item_id !== item.id) };
      return { ...prev, items: [...prev.items, { menu_item_id: item.id, quantity: 1, unit_price: item.price, name: item.name }] };
    });
  };

  const changeQty = (item, delta) => setNewOrder(prev => ({
    ...prev,
    items: prev.items.map(i => i.menu_item_id === item.id ? { ...i, quantity: i.quantity + delta } : i).filter(i => i.quantity > 0),
  }));

  const handleCreateOrder = async () => {
    if (newOrder.items.length === 0) { setError(t('genericError')); return; }
    setSubmitting(true);
    try {
      await ordersAPI.create({ table_id: selectedTable.id, items: newOrder.items, notes: newOrder.notes });
      setSheet(null);
      loadAll();
    } catch (err) { setError(err.response?.data?.message || t('orderCreateError')); }
    finally { setSubmitting(false); }
  };

  const handleAddTable = async () => {
    if (!newTable.name.trim()) { setError(t('tableNameRequired')); return; }
    setSubmitting(true);
    try {
      await tablesAPI.create({
        name: newTable.name,
        capacity: newTable.capacity,
        table_category_id: newTable.table_category_id || null,
      });
      setSheet(null);
      setNewTable({ name: '', capacity: 4, table_category_id: '' });
      loadAll();
    } catch (err) { setError(err.response?.data?.message || t('tableAddError')); }
    finally { setSubmitting(false); }
  };

  const handleBulkAddTables = async () => {
    const count = parseInt(bulkTable.count);
    const start = parseInt(bulkTable.startFrom);
    if (!bulkTable.prefix.trim() || count < 1 || count > 50) { setError(t('invalidPrefixCount')); return; }
    setSubmitting(true);
    try {
      for (let i = 0; i < count; i++) {
        await tablesAPI.create({
          name: `${bulkTable.prefix.trim()} ${start + i}`,
          capacity: bulkTable.capacity,
          table_category_id: bulkTable.table_category_id || null,
        });
      }
      setSheet(null);
      setBulkMode(false);
      setBulkTable({ prefix: t('tableBulkPrefixDefault') || 'Masa', count: 5, startFrom: 1, capacity: 4, table_category_id: '' });
      loadAll();
    } catch (err) { setError(err.response?.data?.message || t('tablesAddError')); }
    finally { setSubmitting(false); }
  };

  const handleOrderStatusChange = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      await loadActiveOrders(selectedTable);
    } catch (err) { setError(err.response?.data?.message || t('statusUpdateError')); }
  };

  const handleDeleteTable = async (table) => {
    const tbl = table || selectedTable;
    if (!await confirm({ message: `"${tbl.name}" ${t('tablesDeleteConfirmSuffix')}`, danger: true })) return;
    setOpenPopover(null);
    try {
      await tablesAPI.delete(tbl.id);
      setSheet(null);
      loadAll();
    } catch (err) { setError(err.response?.data?.message || t('tableDeleteError')); }
  };

  const handleResetTable = async (table) => {
    const tbl = table || selectedTable;
    if (!await confirm({ message: `"${tbl.name}" ${t('tablesCloseConfirmSuffix')}`, danger: true })) return;
    setOpenPopover(null);
    try {
      await tablesAPI.reset(tbl.id);
      setSheet(null);
      loadAll();
    } catch (err) { setError(err.response?.data?.message || t('tableCloseError')); }
  };

  const openCheckout = () => {
    setCheckoutType('cash'); setSplitCash(''); setSplitCard('');
    setDiscount({ mode: 'amount', value: '' });
    setPromoCode(''); setPromoResult(null);
    setVoucherCode(''); setVoucherResult(null);
    setShowDiscountExtras(false);
    setSheet('checkout');
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true); setPromoResult(null);
    try {
      const res = await promoCodesAPI.validate({ code: promoCode, order_amount: grossTotal });
      setPromoResult({ valid: true, promo: res.data.promo, discount_amount: res.data.discount_amount });
    } catch (err) {
      setPromoResult({ valid: false, message: err.response?.data?.message || t('invalidPromoCode') });
    } finally { setPromoLoading(false); }
  };

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherLoading(true); setVoucherResult(null);
    try {
      const res = await giftVouchersAPI.validate({ code: voucherCode });
      setVoucherResult({ valid: true, voucher: res.data.voucher });
    } catch (err) {
      setVoucherResult({ valid: false, message: err.response?.data?.message || t('invalidGiftVoucher') });
    } finally { setVoucherLoading(false); }
  };

  /* ── Category management ── */


  const handleSaveCat = async () => {
    if (!catForm.name.trim()) return;
    setCatSaving(true);
    try {
      if (editCat) await tableCategoriesAPI.update(editCat.id, catForm);
      else         await tableCategoriesAPI.create(catForm);
      setEditCat(null);
      setCatForm({ name: '', color: '#6b7280' });
      const res = await tableCategoriesAPI.getAll();
      setTableCategories(res.data.data || []);
    } catch (err) { setError(err.response?.data?.message || t('categorySaveError')); }
    finally { setCatSaving(false); }
  };

  const handleDeleteCat = async (cat) => {
    if (!await confirm({ message: `"${cat.name}" ${t('tablesCategoryDeleteConfirmSuffix')}`, danger: true })) return;
    try {
      await tableCategoriesAPI.delete(cat.id);
      if (categoryFilter === cat.id) setCategoryFilter('all');
      const res = await tableCategoriesAPI.getAll();
      setTableCategories(res.data.data || []);
      await loadAll();
    } catch (err) { setError(err.response?.data?.message || t('categoryDeleteError')); }
  };

  /* ── Checkout calculations ── */
  const grossTotal   = activeOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
  const discountAmt  = discount.mode === 'percent'
    ? grossTotal * (Math.min(parseFloat(discount.value) || 0, 100) / 100)
    : Math.min(parseFloat(discount.value) || 0, grossTotal);
  const promoAmt     = (promoResult?.valid && promoResult.discount_amount) ? parseFloat(promoResult.discount_amount) : 0;
  const voucherAmt   = (voucherResult?.valid && voucherResult.voucher)
    ? Math.min(parseFloat(voucherResult.voucher.remaining_amount), Math.max(0, grossTotal - discountAmt - promoAmt))
    : 0;
  const totalDiscount = discountAmt + promoAmt + voucherAmt;
  const netTotal     = Math.max(0, grossTotal - totalDiscount);
  const splitC       = parseFloat(splitCash) || 0;
  const splitK       = parseFloat(splitCard) || 0;
  const splitValid   = checkoutType === 'split' && splitC > 0 && splitK > 0 && Math.abs(splitC + splitK - netTotal) < 0.01;
  const canCheckout  = !submitting && activeOrders.length > 0 && (checkoutType !== 'split' || splitValid);

  // Müşteri arama (client-side filter — backend search endpoint yoksa)
  const searchCustomers = async (query) => {
    if (!query || query.length < 2) { setCustomerResults([]); return; }
    try {
      const res = await customersAPI.getAll();
      const all = res.data?.data || res.data || [];
      const q = query.toLowerCase();
      setCustomerResults((Array.isArray(all) ? all : []).filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      ).slice(0, 10));
    } catch { /* ignore */ }
  };

  // Checkout sheet kapanırken müşteri seçici state'ini sıfırla
  useEffect(() => {
    if (sheet !== 'checkout') {
      setCustomerSearch('');
      setCustomerResults([]);
      setSelectedCustomer(null);
    }
  }, [sheet]);

  const handleCheckout = async () => {
    setSubmitting(true); setError('');
    try {
      const body = {
        payment_type: checkoutType,
        discount_amount: totalDiscount,
        customer_id: selectedCustomer?.id || null,
        ...(promoResult?.valid && { promo_code_id: promoResult.promo.id }),
      };
      if (checkoutType === 'split') { body.cash_amount = splitC; body.card_amount = splitK; }
      await paymentsAPI.tableCheckout(selectedTable.id, body);

      // Hediye çeki kullanıldıysa redeem et
      if (voucherResult?.valid && voucherAmt > 0) {
        await giftVouchersAPI.redeem(voucherResult.voucher.id, { amount: voucherAmt });
      }

      printReceipt({
        restaurantName,
        tableName: selectedTable.name || `${t('tablesTablePrefix')} ${selectedTable.id}`,
        orders: activeOrders,
        grossTotal,
        discountAmt: totalDiscount,
        netTotal,
        paymentType: checkoutType,
        cashAmount: splitC,
        cardAmount: splitK,
        t,
        lang,
      });
      setSheet(null);
      loadAll();
    } catch (err) { setError(err.response?.data?.message || t('paymentFailed')); }
    finally { setSubmitting(false); }
  };

  /* ── Derived ── */
  const filteredMenu  = menuFilter === 'all' ? menuItems : menuItems.filter(i => i.category_id === parseInt(menuFilter));
  const orderTotal    = newOrder.items.reduce((s, i) => s + parseFloat(i.unit_price) * i.quantity, 0);
  const occupiedCount = tables.filter(tbl => tbl.status === 'occupied').length;
  const emptyCount    = tables.filter(tbl => tbl.status === 'empty').length;

  if (loading) return (
    <div className="pb-6">
      <SkeletonList count={12} variant="card" />
    </div>
  );

  return (
    <div className="pb-6" onClick={() => setOpenPopover(null)}>
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl mb-4 text-sm flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} aria-label={t('close')} className="w-10 h-10 md:w-auto md:h-auto md:text-lg leading-none ml-2 flex items-center justify-center rounded-lg active:bg-red-100 dark:active:bg-red-900/40">✕</button>
        </div>
      )}

      {/* ── Waiter Call Notifications ── */}
      {waiterCalls.length > 0 && (
        <div className="mb-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-2xl overflow-hidden">
          <button
            onClick={() => setCallsExpanded(v => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
          >
            <span className="text-lg animate-bounce">🔔</span>
            <span className="font-bold text-orange-800 dark:text-orange-300 text-sm flex-1">
              {t('tablesWaiterCallTitle')} — {waiterCalls.length} {t('tablesWaitingSuffix')}
            </span>
            <span className="text-orange-500 text-xs">{callsExpanded ? t('hide') : t('show')}</span>
          </button>
          {callsExpanded && (
            <div className="divide-y divide-orange-100 dark:divide-orange-800">
              {waiterCalls.map(call => (
                <div key={call.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-200">
                      {call.table_name ? `${t('tableNo')}: ${call.table_name}` : t('tableNotSpecified')}
                      {call.status === 'acknowledged' && (
                        <span className="ml-2 text-xs font-normal text-orange-500">{t('seenLabel')}</span>
                      )}
                    </p>
                    {call.note && <p className="text-xs text-orange-600 dark:text-orange-400 truncate mt-0.5">{call.note}</p>}
                    <p className="text-xs text-orange-400 dark:text-orange-500 mt-0.5">
                      {new Date(call.called_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {call.status === 'pending' && (
                      <button
                        onClick={() => acknowledgeCall(call.id)}
                        className="text-xs px-3 py-2.5 md:py-1.5 min-h-[40px] md:min-h-0 rounded-lg bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 font-semibold hover:bg-orange-300 dark:hover:bg-orange-700 active:scale-[0.97] transition-all"
                      >
                        {t('markSeen')}
                      </button>
                    )}
                    <button
                      onClick={() => resolveCall(call.id)}
                      className="text-xs px-3 py-2.5 md:py-1.5 min-h-[40px] md:min-h-0 rounded-lg bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 font-semibold hover:bg-green-200 dark:hover:bg-green-900/60 active:scale-[0.97] transition-all"
                    >
                      {t('resolvedLabel')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Stat Overview (Dolu / Boş / Rezerve / Toplam Kapasite) ── */}
      {tables.length > 0 && (() => {
        const stats = tableStats;
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 sm:p-4 border border-red-100 dark:border-red-900/40">
              <div className="flex items-center gap-2 mb-1">
                <span aria-hidden="true" className="text-base sm:text-lg">🍽️</span>
                <p className="text-xs text-red-600 dark:text-red-400 font-medium truncate">{t('tableStatOccupied') || 'Dolu'}</p>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-red-700 dark:text-red-300">{stats.occupied}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 sm:p-4 border border-green-100 dark:border-green-900/40">
              <div className="flex items-center gap-2 mb-1">
                <span aria-hidden="true" className="text-base sm:text-lg">✅</span>
                <p className="text-xs text-green-600 dark:text-green-400 font-medium truncate">{t('tableStatFree') || 'Boş'}</p>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-green-700 dark:text-green-300">{stats.empty}</p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 sm:p-4 border border-yellow-100 dark:border-yellow-900/40">
              <div className="flex items-center gap-2 mb-1">
                <span aria-hidden="true" className="text-base sm:text-lg">📅</span>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 font-medium truncate">{t('tableStatReserved') || 'Rezerve'}</p>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-yellow-700 dark:text-yellow-300">{stats.reserved}</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 sm:p-4 border border-blue-100 dark:border-blue-900/40">
              <div className="flex items-center gap-2 mb-1">
                <span aria-hidden="true" className="text-base sm:text-lg">👥</span>
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium truncate">{t('tableStatCapacity') || 'Toplam Kapasite'}</p>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.capacity}</p>
            </div>
          </div>
        );
      })()}

      {/* ── Toolbar ── */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('searchTableHint')}
            className="w-full pl-9 pr-4 py-3 md:py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors" />
          {search && <button onClick={() => setSearch('')} aria-label={t('clear')} className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg active:bg-gray-100 dark:active:bg-gray-700">✕</button>}
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1 gap-1 md:gap-0.5 flex-1 sm:flex-none">
            {[
              { val: 'all',      label: `${t('all')} (${tables.length})` },
              { val: 'empty',    label: `${t('statusEmpty')} (${emptyCount})` },
              { val: 'occupied', label: `${t('statusOccupied')} (${occupiedCount})` },
            ].map(f => (
              <button key={f.val} onClick={() => setStatusFilter(f.val)}
                className={`px-3 py-2.5 md:py-1.5 flex-1 sm:flex-none rounded-lg text-xs font-semibold transition-all active:scale-[0.97] whitespace-nowrap ${
                  statusFilter === f.val
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
          <button onClick={(e) => { e.stopPropagation(); setNewTable({ name: '', capacity: 4, table_category_id: '' }); setBulkMode(false); setSheet('add-table'); }}
            className="bg-blue-600 active:bg-blue-800 text-white px-4 py-3 md:py-2.5 rounded-xl text-sm font-bold flex items-center gap-1 transition-all active:scale-95 whitespace-nowrap focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none">
            {t('addTableShort')}
          </button>
        </div>
      </div>

      {/* ── Sort chips ── */}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto scrollbar-none pb-1 -mx-4 px-4 md:mx-0 md:px-0">
        <span className="flex-shrink-0 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('sortBy') || 'Sırala'}:</span>
        {[
          { val: 'name',     label: t('sortByName')     || 'Adına göre' },
          { val: 'capacity', label: t('sortByCapacity') || 'Kapasite' },
          { val: 'status',   label: t('sortByStatus')   || 'Durum' },
          { val: 'duration', label: t('sortByDuration') || 'Süre' },
        ].map(s => {
          const active = sortKey === s.val;
          return (
            <button key={s.val} onClick={() => setSortKey(s.val)}
              aria-pressed={active}
              className={`flex-shrink-0 px-3 py-2 md:py-1.5 rounded-xl text-xs font-semibold transition-all active:scale-[0.97] whitespace-nowrap border ${
                active
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-300'
              }`}>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* ── Category filter tabs ── */}
      {tableCategories.length > 0 && (
        <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-none pb-1 -mx-4 px-4 md:mx-0 md:px-0">
          <button onClick={() => setCategoryFilter('all')}
            className={`flex-shrink-0 px-3 py-2.5 md:py-1.5 rounded-xl text-xs font-semibold transition-all active:scale-[0.97] whitespace-nowrap ${
              categoryFilter === 'all'
                ? 'bg-gray-800 dark:bg-white text-white dark:text-gray-900'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}>
            {t('allFloors')}
          </button>
          {sortedTableCategories.map((cat, idx) => {
            const count = tables.filter(tbl => tbl.table_category_id === cat.id).length;
            const isActive = categoryFilter === cat.id;
            const isDragging = draggedIdx === idx;
            const isDropTarget = dragOverIdx === idx && draggedIdx !== null && draggedIdx !== idx;
            return (
              <button key={cat.id} onClick={() => setCategoryFilter(isActive ? 'all' : cat.id)}
                draggable
                onDragStart={handleCatDragStart(idx)}
                onDragOver={handleCatDragOver(idx)}
                onDrop={handleCatDrop(idx)}
                onDragEnd={() => { setDraggedIdx(null); setDragOverIdx(null); }}
                aria-label={t('categoryChipDragHint') || `${cat.name} — ${t('dragToReorder') || 'sürükleyerek sıralayın'}`}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 md:py-1.5 rounded-xl text-xs font-semibold transition-all active:scale-[0.97] whitespace-nowrap border-2 max-w-[180px] cursor-grab active:cursor-grabbing ${
                  isDragging ? 'opacity-50' : ''
                } ${
                  isDropTarget ? 'border-blue-500' : (isActive ? 'text-white border-transparent' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-300')
                }`}
                style={isActive && !isDropTarget ? { backgroundColor: cat.color, borderColor: cat.color } : (isActive ? { backgroundColor: cat.color } : {})}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.7)' : cat.color }} />
                <span className="truncate">{cat.name}</span>
                <span className={`flex-shrink-0 ${isActive ? 'text-white/70' : 'text-gray-400'}`}>({count})</span>
              </button>
            );
          })}
          <button onClick={(e) => { e.stopPropagation(); setCatForm({ name: '', color: '#6b7280' }); setEditCat(null); setSheet('manage-categories'); }}
            className="flex-shrink-0 px-3 py-2.5 md:py-1.5 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 active:scale-[0.97] transition-all whitespace-nowrap">
            {t('categoriesNav')}
          </button>
        </div>
      )}
      {tableCategories.length === 0 && (
        <div className="mb-4">
          <button onClick={(e) => { e.stopPropagation(); setCatForm({ name: '', color: '#3b82f6' }); setEditCat(null); setSheet('manage-categories'); }}
            className="text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1">
            {t('addFloorCategory')}
          </button>
        </div>
      )}

      {/* ── Table Grid ── */}
      {filteredTables.length === 0 ? (
        tables.length === 0 ? (
          <EmptyState
            icon="🪑"
            title={t('tableNoItemsTitle') || 'Henüz masa yok'}
            description={t('tableNoItemsDesc') || 'İlk masanı ekleyerek sipariş yönetimine başla.'}
            actionLabel={t('tableAddBtn') || '+ Yeni Masa'}
            onAction={() => { setNewTable({ name: '', capacity: 4, table_category_id: '' }); setBulkMode(false); setSheet('add-table'); }}
          />
        ) : categoryFilter !== 'all' ? (
          <EmptyState
            icon="🔍"
            title={t('tableNoCategoryItemsTitle') || 'Bu kategoride masa yok'}
            description={t('tableNoCategoryItemsDesc') || 'Farklı bir kategori seçin veya bu kategoriye masa ekleyin.'}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <div className="text-5xl mb-3">{search ? '🔍' : '🍽️'}</div>
            <p className="font-medium">{search ? `"${search}" — ${t('noTableMatching')}` : t('noTablesYet')}</p>
          </div>
        )
      ) : (
        // auto-rows-fr: aynı row'daki tüm masa kartları en uzun olana göre stretch — dolu masa içeriği fazla olunca boş masalarla yükseklik farkı oluşmasın.
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3 md:gap-2 auto-rows-fr">
          {filteredTables.map(table => {
            const isOccupied   = table.status === 'occupied';
            // Reserved: backend status veya frontend reservation join (`reservedTableIds` Set)
            const isReserved   = !isOccupied && (table.status === 'reserved' || reservedTableIds.has(table.id));
            const pendingOrders = parseInt(table.pending_orders) || 0;
            const pendingTotal  = parseFloat(table.pending_total) || 0;

            // 3-state visual: kırmızı (dolu) / amber (rezerve) / beyaz (boş)
            const cardCls = isOccupied
              ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700 hover:border-red-400'
              : isReserved
                ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 hover:border-amber-400'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600';
            const dotCls = isOccupied
              ? 'bg-red-500 animate-pulse'
              : isReserved
                ? 'bg-amber-500'
                : 'bg-green-500';
            const statusLabel = isOccupied
              ? (t('statusOccupied') || 'Dolu')
              : isReserved
                ? (t('statusReserved') || 'Rezerve')
                : (t('statusEmpty') || 'Boş');

            return (
              <div key={table.id} className="relative group h-full">
                <button
                  onClick={() => handleTableClick(table)}
                  aria-label={`${t('tablesTablePrefix') || 'Masa'} ${table.name} — ${statusLabel}`}
                  className={`w-full h-full text-left rounded-xl p-3 transition-all active:scale-[0.97] select-none border-2 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none flex flex-col ${cardCls}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotCls}`} />
                    <div className="w-7 md:w-5" />
                  </div>

                  <div className="font-bold text-gray-900 dark:text-white text-sm leading-tight truncate mb-1">
                    {table.name}
                  </div>

                  {/* Category badge */}
                  {table.category_name && (
                    <div className="text-xs md:text-[10px] font-semibold truncate mb-1 flex items-center gap-1"
                      style={{ color: table.category_color || '#6b7280' }}>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: table.category_color || '#6b7280' }} />
                      <span className="truncate">{table.category_name}</span>
                    </div>
                  )}

                  {isOccupied ? (
                    <div className="space-y-1 flex-1 flex flex-col justify-end">
                      <div className="text-xs text-red-600 dark:text-red-400 font-semibold">{pendingOrders} {t('ordersSuffix')}</div>
                      {pendingTotal > 0 && <div className="text-xs font-bold text-gray-700 dark:text-gray-300">₺{pendingTotal.toFixed(0)}</div>}
                      {table.oldest_pending_at && (() => {
                        const min = Math.max(0, Math.floor((Date.now() - new Date(table.oldest_pending_at).getTime()) / 60000));
                        const cls = min < 30
                          ? 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300'
                          : min < 90
                            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                            : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-semibold';
                        const label = min < 60
                          ? `${min}${t('kitchenMinShort')}`
                          : min < 120
                            ? `1${t('tableDurationHourShort')} ${min - 60}${t('kitchenMinShort')}`
                            : `2${t('tableDurationHourShort')}+`;
                        return (
                          <div
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium ${cls}`}
                            aria-label={`${t('tableSeatingDuration') || 'Oturma süresi'}: ${label}`}
                            title={t('tableSeatingDuration') || 'Oturma süresi'}
                          >
                            <span aria-hidden="true">🕐</span>
                            <span>{label}</span>
                          </div>
                        );
                      })()}
                    </div>
                  ) : isReserved ? (
                    <div className="text-xs text-amber-600 dark:text-amber-400 font-medium flex-1 flex items-end">
                      📅 {t('statusReserved') || 'Rezerve'}
                    </div>
                  ) : (
                    <div className="text-xs text-green-600 dark:text-green-400 font-medium flex-1 flex items-end">{t('statusEmpty')}</div>
                  )}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); setOpenPopover(openPopover === table.id ? null : table.id); }}
                  aria-label={`${table.name} ${t('tablesManageAriaSuffix')}`}
                  className="absolute top-1 right-1 w-10 h-10 md:w-6 md:h-6 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-300 bg-gray-100/80 dark:bg-gray-700/80 md:bg-transparent md:dark:bg-transparent hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-90 transition-all opacity-90 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 text-base md:text-[12px] font-bold">
                  ⋯
                </button>

                {openPopover === table.id && (
                  <ManagePopover
                    table={table}
                    onClose={() => setOpenPopover(null)}
                    onCreateOrder={() => { setOpenPopover(null); openCreateOrder(table); }}
                    onReset={() => handleResetTable(table)}
                    onDelete={() => handleDeleteTable(table)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Sheet: Active Orders ── */}
      {sheet === 'active-orders' && selectedTable && (
        <BottomSheet title={t('ordersSheetTitle')} subtitle={selectedTable.name} onClose={() => setSheet(null)} wide>
          {activeOrders.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-4xl mb-3">✅</div>
              <p className="text-gray-500 font-medium">{t('noPendingOrders')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 mb-3">
                {activeOrders.map(order => {
                  const st = statusMap[order.status] || statusMap.preparing;
                  return (
                    <div key={order.id} className="bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700/50">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-500">#{order.id}</span>
                          {order.waiter_name && <span className="text-xs text-gray-400">· {order.waiter_name}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-md font-semibold ${st.bg} ${st.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
                          </span>
                          <span className="text-sm font-bold text-gray-900 dark:text-white">₺{parseFloat(order.total_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="px-3 py-2 flex flex-wrap gap-x-3 gap-y-0.5">
                        {(order.items || []).map(item => (
                          <span key={item.id} className="text-xs text-gray-600 dark:text-gray-300">
                            {item.item_name || item.name} <span className="text-gray-400">×{item.quantity}</span>
                          </span>
                        ))}
                        {order.notes && <span className="text-xs text-amber-600 dark:text-amber-400 w-full mt-0.5">📝 {order.notes}</span>}
                      </div>
                      {(order.status === 'preparing' || order.status === 'ready') && (
                        <div className="px-3 pb-2 flex gap-2">
                          {order.status === 'preparing' && (
                            <button onClick={() => handleOrderStatusChange(order.id, 'ready')}
                              className="text-xs px-4 py-2.5 md:py-1.5 min-h-[40px] md:min-h-0 rounded-lg bg-amber-500 active:bg-amber-700 active:scale-[0.97] text-white font-semibold transition-all">{t('markReadyShort')}</button>
                          )}
                          {order.status === 'ready' && (
                            <button onClick={() => handleOrderStatusChange(order.id, 'served')}
                              className="text-xs px-4 py-2.5 md:py-1.5 min-h-[40px] md:min-h-0 rounded-lg bg-blue-500 active:bg-blue-700 active:scale-[0.97] text-white font-semibold transition-all">{t('markServed')}</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="sticky bottom-0 -mx-5 -mb-4 px-5 pt-3 pb-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 space-y-2.5">
                <div className="flex justify-between items-center px-1">
                  <span className="text-sm text-gray-500">{activeOrders.length} {t('orderSubtotal')}</span>
                  <span className="text-xl font-black text-gray-900 dark:text-white">₺{grossTotal.toFixed(2)}</span>
                </div>
                <BigButton onClick={openCheckout} color="green">{t('closeCheck')}</BigButton>
                <BigButton onClick={() => openCreateOrder(selectedTable)} color="blue" small>{t('addNewOrder')}</BigButton>
              </div>
            </>
          )}
        </BottomSheet>
      )}

      {/* ── Sheet: Checkout ── */}
      {sheet === 'checkout' && selectedTable && (
        <BottomSheet title={t('checkoutTitle')} subtitle={selectedTable.name} wide="checkout" onClose={() => setSheet('active-orders')}>
          {/* 2-column layout on md+ */}
          <div className="md:grid md:grid-cols-2 md:gap-6">

            {/* ── LEFT: Siparişler + İndirimler ── */}
            <div className="space-y-4">
              {/* Order list */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl divide-y divide-gray-100 dark:divide-gray-700">
                {activeOrders.map(order => (
                  <div key={order.id} className="flex justify-between items-center px-3 py-2 text-sm">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('orderPrefix')} #{order.id}
                      {order.waiter_name && <span className="text-xs text-gray-400 ml-1">· {order.waiter_name}</span>}
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">₺{parseFloat(order.total_amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* İskonto */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">🎁 {t('discountAndTreat')}</span>
                  <div className="flex bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-700 overflow-hidden text-xs">
                    {['amount', 'percent'].map(m => (
                      <button key={m} onClick={() => setDiscount(d => ({ ...d, mode: m, value: '' }))}
                        className={`px-3 py-1.5 font-semibold transition-colors ${discount.mode === m ? 'bg-amber-500 text-white' : 'text-amber-600 dark:text-amber-400'}`}>
                        {m === 'amount' ? `₺ ${t('amountMode')}` : `% ${t('percentMode')}`}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold">{discount.mode === 'percent' ? '%' : '₺'}</span>
                    <input type="number" inputMode="decimal" min="0" max={discount.mode === 'percent' ? 100 : grossTotal}
                      step={discount.mode === 'percent' ? 1 : 0.01} value={discount.value}
                      onChange={e => setDiscount(d => ({ ...d, value: e.target.value }))} placeholder="0"
                      className="w-full pl-7 pr-3 py-3 md:py-2.5 border border-amber-200 dark:border-amber-700 rounded-xl text-sm font-bold bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-amber-400" />
                  </div>
                  {discountAmt > 0 && <span className="text-sm font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap">−₺{discountAmt.toFixed(2)}</span>}
                </div>
                {discount.mode === 'percent' && (
                  <div className="flex gap-2 md:gap-1.5 mt-2 flex-wrap">
                    {[10, 20, 50, 100].map(v => (
                      <button key={v} onClick={() => setDiscount(d => ({ ...d, value: String(v) }))}
                        className="text-xs px-3 py-2 md:py-1 md:px-2.5 min-h-[36px] md:min-h-0 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-semibold active:scale-[0.97] transition-all">
                        %{v}{v === 100 ? ` ${t('treatLabel')}` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Kampanya & Hediye Çeki — collapsible */}
              <details className="mb-3 group" open={!!promoResult?.valid || !!voucherResult?.valid}>
                <summary className="cursor-pointer flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700">
                  <span>
                    🏷️ {t('promoCodeAndVoucher')}
                    {(promoResult?.valid || voucherResult?.valid) && (
                      <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-bold">
                        −₺{(promoAmt + voucherAmt).toFixed(2)}
                      </span>
                    )}
                  </span>
                  <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 space-y-2">
                  <div className="space-y-3">
                    {/* Kampanya Kodu */}
                    <div className="border border-purple-200 dark:border-purple-800 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">🏷️ {t('promoCodeShort')}</span>
                        {promoResult?.valid && (
                          <button onClick={() => { setPromoResult(null); setPromoCode(''); }}
                            className="text-xs text-gray-400 hover:text-red-500 active:scale-[0.97] transition-all px-2 py-2 md:py-0 min-h-[36px] md:min-h-0">✕ {t('removeAction')}</button>
                        )}
                      </div>
                      {promoResult?.valid ? (
                        <div className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/20 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-xs font-bold text-purple-700 dark:text-purple-300">{promoResult.promo.code}</p>
                            <p className="text-xs text-purple-600 dark:text-purple-400">{promoResult.promo.name}</p>
                          </div>
                          <span className="text-sm font-black text-purple-700 dark:text-purple-300">−₺{promoAmt.toFixed(2)}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex gap-2">
                            <input value={promoCode} onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                              onKeyDown={e => e.key === 'Enter' && handleValidatePromo()}
                              autoCapitalize="characters"
                              className="flex-1 border border-purple-200 dark:border-purple-700 rounded-xl px-3 py-3 md:py-2.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono tracking-wider focus:outline-none focus:border-purple-400"
                              placeholder={t('tablePromoCodePlaceholder') || 'HOSGELDIN10'} />
                            <button onClick={handleValidatePromo} disabled={promoLoading || !promoCode.trim()}
                              className="px-4 py-3 md:py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 active:scale-95 transition-all">
                              {promoLoading ? '...' : t('apply')}
                            </button>
                          </div>
                          {promoResult?.valid === false && (
                            <p className="text-xs text-red-500 mt-1.5">{promoResult.message}</p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Hediye Çeki */}
                    <div className="border border-emerald-200 dark:border-emerald-800 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">🎟️ {t('giftVoucher')}</span>
                        {voucherResult?.valid && (
                          <button onClick={() => { setVoucherResult(null); setVoucherCode(''); }}
                            className="text-xs text-gray-400 hover:text-red-500 active:scale-[0.97] transition-all px-2 py-2 md:py-0 min-h-[36px] md:min-h-0">✕ {t('removeAction')}</button>
                        )}
                      </div>
                      {voucherResult?.valid ? (
                        <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300">{voucherResult.voucher.code}</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400">
                              {t('balanceLabel')}: ₺{parseFloat(voucherResult.voucher.remaining_amount).toFixed(2)}
                              {voucherResult.voucher.customer_name && ` · ${voucherResult.voucher.customer_name}`}
                            </p>
                          </div>
                          <span className="text-sm font-black text-emerald-700 dark:text-emerald-300">−₺{voucherAmt.toFixed(2)}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex gap-2">
                            <input value={voucherCode} onChange={e => { setVoucherCode(e.target.value.toUpperCase()); setVoucherResult(null); }}
                              onKeyDown={e => e.key === 'Enter' && handleValidateVoucher()}
                              autoCapitalize="characters"
                              className="flex-1 border border-emerald-200 dark:border-emerald-700 rounded-xl px-3 py-3 md:py-2.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono tracking-wider focus:outline-none focus:border-emerald-400"
                              placeholder={t('tableVoucherCodePlaceholder') || 'GV-KARAD100'} />
                            <button onClick={handleValidateVoucher} disabled={voucherLoading || !voucherCode.trim()}
                              className="px-4 py-3 md:py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 active:scale-95 transition-all">
                              {voucherLoading ? '...' : t('apply')}
                            </button>
                          </div>
                          {voucherResult?.valid === false && (
                            <p className="text-xs text-red-500 mt-1.5">{voucherResult.message}</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </details>
            </div>

            {/* ── RIGHT: Tutar + Ödeme + Butonlar ── */}
            <div className="mt-4 md:mt-0 flex flex-col gap-4">
              {/* Tutar özeti */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3">
                {totalDiscount > 0 && (
                  <div className="space-y-1 mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{t('subtotal')}</span>
                      <span>₺{grossTotal.toFixed(2)}</span>
                    </div>
                    {discountAmt > 0 && <div className="flex justify-between text-xs text-amber-600 dark:text-amber-400"><span>{t('discount')}</span><span>−₺{discountAmt.toFixed(2)}</span></div>}
                    {promoAmt > 0 && <div className="flex justify-between text-xs text-purple-600 dark:text-purple-400"><span>{t('promoCodeShort')} ({promoResult.promo.code})</span><span>−₺{promoAmt.toFixed(2)}</span></div>}
                    {voucherAmt > 0 && <div className="flex justify-between text-xs text-emerald-600 dark:text-emerald-400"><span>{t('giftVoucher')} ({voucherResult.voucher.code})</span><span>−₺{voucherAmt.toFixed(2)}</span></div>}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('amountToPay')}</div>
                  <span className="text-3xl font-black text-gray-900 dark:text-white">₺{netTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Müşteri seçici (opsiyonel — sadakat / yaşam boyu değer için) */}
              <div className="mb-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {t('tablesCustomerOptionalLabel') || 'Müşteri (opsiyonel — sadakat için)'}
                </label>
                <input
                  type="search"
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); searchCustomers(e.target.value); }}
                  placeholder={t('customerSearchPlaceholder') || 'İsim/telefon ara...'}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700"
                />
                {customerResults.length > 0 && !selectedCustomer && (
                  <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded-lg max-h-40 overflow-y-auto bg-white dark:bg-gray-800 shadow">
                    {customerResults.slice(0, 6).map(c => (
                      <button key={c.id} type="button"
                        onClick={() => { setSelectedCustomer(c); setCustomerSearch(`${c.name} (${c.phone || ''})`); setCustomerResults([]); }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700 last:border-0">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-gray-500">{c.phone} · {c.email || '-'}</div>
                      </button>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <div className="mt-1 flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-1.5 rounded-lg text-sm">
                    <span>✓ {selectedCustomer.name}</span>
                    <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }} className="text-red-500 text-xs">× {t('tablesCustomerRemoveBtn') || 'Kaldır'}</button>
                  </div>
                )}
              </div>

              {/* Ödeme yöntemi */}
              {netTotal > 0 && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    {[['cash', '💵', t('cash')], ['card', '💳', t('card')], ['split', '💵💳', t('paymentMixed')]].map(([val, icon, label]) => (
                      <button key={val} onClick={() => { setCheckoutType(val); setSplitCash(''); setSplitCard(''); }}
                        className={`py-3.5 rounded-xl border-2 font-semibold flex flex-col items-center gap-1 active:scale-[0.97] transition-all ${
                          checkoutType === val ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                        <span className="text-xl">{icon}</span>
                        <span className="text-xs">{label}</span>
                      </button>
                    ))}
                  </div>
                  {checkoutType === 'split' && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 space-y-2.5">
                      {[
                        { label: `💵 ${t('cash')}`, val: splitCash, set: v => { setSplitCash(v); setSplitCard(Math.max(0, netTotal - (parseFloat(v) || 0)).toFixed(2)); } },
                        { label: `💳 ${t('card')}`,  val: splitCard, set: v => { setSplitCard(v); setSplitCash(Math.max(0, netTotal - (parseFloat(v) || 0)).toFixed(2)); } },
                      ].map(({ label, val, set }) => (
                        <div key={label} className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-20 flex-shrink-0">{label}</span>
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold">₺</span>
                            <input type="number" inputMode="decimal" step="0.01" min="0" max={netTotal} value={val} onChange={e => set(e.target.value)}
                              className="w-full pl-7 pr-3 py-3 md:py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-bold bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-400 outline-none" placeholder="0.00" />
                          </div>
                        </div>
                      ))}
                      <div className={`text-center text-xs font-semibold py-1 rounded-lg ${splitValid ? 'text-green-600' : 'text-red-500'}`}>
                        {splitC + splitK === 0 ? '' : splitValid ? `✓ ${t('amountsMatch')}` : `${t('remainingLabel')}: ₺${Math.abs(netTotal - splitC - splitK).toFixed(2)}`}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Butonlar */}
              <div className="sticky bottom-0 -mx-5 md:mx-0 px-5 md:px-0 pt-3 pb-1 md:pt-0 md:pb-0 bg-white dark:bg-gray-900 md:bg-transparent md:dark:bg-transparent border-t md:border-0 border-gray-200 dark:border-gray-700 space-y-2.5 mt-auto z-10">
                {netTotal === 0
                  ? <BigButton onClick={handleCheckout} color="green" disabled={submitting}>{submitting ? `⏳ ${t('processing')}` : `🎁 ${t('treatCloseBill')}`}</BigButton>
                  : <BigButton onClick={handleCheckout} color="green" disabled={!canCheckout}>{submitting ? `⏳ ${t('processing')}` : `✓ ₺${netTotal.toFixed(2)} ${t('takePayment')}`}</BigButton>
                }
                <BigButton onClick={() => setSheet('active-orders')} color="gray" small>{t('back')}</BigButton>
              </div>
            </div>

          </div>
        </BottomSheet>
      )}

      {/* ── Sheet: Create Order ── */}
      {sheet === 'create-order' && selectedTable && (
        <BottomSheet title={t('createOrderTitle')} subtitle={selectedTable.name} onClose={() => setSheet(null)} wide="order">
          {/* ── LEFT: Category list ── */}
          <div className="w-32 sm:w-40 md:w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto app-scroll py-3 px-2 space-y-1">
            <button
              onClick={() => setMenuFilter('all')}
              className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.97] truncate ${menuFilter === 'all' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
              🍽️ {t('filterAll')}
            </button>
            {menuCategories.map(cat => (
              <button key={cat.id}
                onClick={() => setMenuFilter(cat.id)}
                className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.97] truncate ${menuFilter === cat.id ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                {cat.name}
              </button>
            ))}
          </div>

          {/* ── RIGHT: Products + note + summary + button ── */}
          <div className="flex-1 overflow-y-auto app-scroll flex flex-col">
            <div className="flex-1 p-4 space-y-2">
              {filteredMenu.map(item => {
                const sel = newOrder.items.find(i => i.menu_item_id === item.id);
                return (
                  <div key={item.id}
                    className={`flex items-center rounded-2xl p-3 border-2 transition-colors ${sel ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50'}`}>
                    <div className="flex-1 min-w-0" onClick={() => !sel && toggleItem(item)}>
                      <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm truncate">{item.name}</div>
                      <div className="text-blue-600 dark:text-blue-400 font-bold text-sm">₺{parseFloat(item.price).toFixed(2)}</div>
                    </div>
                    {sel ? (
                      <div className="flex items-center gap-1.5 md:gap-1 ml-3">
                        <button onClick={() => changeQty(item, -1)} aria-label={t('posDecreaseQty')} className="w-11 h-11 md:w-9 md:h-9 rounded-xl bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-red-500 font-bold text-lg flex items-center justify-center active:scale-90 transition-transform">−</button>
                        <span className="w-8 text-center font-bold text-blue-600 dark:text-blue-400 text-base">{sel.quantity}</span>
                        <button onClick={() => changeQty(item, 1)} aria-label={t('posIncreaseQty')} className="w-11 h-11 md:w-9 md:h-9 rounded-xl bg-blue-600 text-white font-bold text-lg flex items-center justify-center active:scale-90 transition-transform">+</button>
                      </div>
                    ) : (
                      <button onClick={() => toggleItem(item)} aria-label={t('add')} className="ml-3 w-11 h-11 md:w-9 md:h-9 rounded-xl bg-blue-600 text-white font-bold text-xl flex items-center justify-center active:scale-90 transition-transform">+</button>
                    )}
                  </div>
                );
              })}
              {filteredMenu.length === 0 && <p className="text-center text-gray-400 py-10">{t('noProductInCategory')}</p>}
            </div>

            {/* Sticky bottom: note + summary + button */}
            <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-4 space-y-3 bg-white dark:bg-gray-900">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('noteOptionalLabel')}</label>
                <textarea value={newOrder.notes} onChange={e => setNewOrder({ ...newOrder, notes: e.target.value })} rows={2}
                  className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none" placeholder={t('orderNotePlaceholder')} />
              </div>
              {newOrder.items.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-3">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">{newOrder.items.length} {t('itemSuffix')}</span>
                    <span className="font-bold text-blue-700 dark:text-blue-300">₺{orderTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {newOrder.items.map(i => (
                      <span key={i.menu_item_id} className="text-xs bg-white dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-lg border border-blue-200 dark:border-blue-700">
                        {i.name} ×{i.quantity}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <BigButton onClick={handleCreateOrder} color="green" disabled={submitting || newOrder.items.length === 0}>
                {submitting ? `⏳ ${t('creating')}` : `✓ ${t('createOrderBtn')}${newOrder.items.length > 0 ? ` (${newOrder.items.length} ${t('itemSuffix')})` : ''}`}
              </BigButton>
            </div>
          </div>
        </BottomSheet>
      )}

      {/* ── Sheet: Add Table ── */}
      {sheet === 'add-table' && (
        <BottomSheet title={t('addNewTable')} onClose={() => { setSheet(null); setBulkMode(false); }}>
          <div className="space-y-4">
            {/* Mode toggle */}
            <div className="flex rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700">
              <button onClick={() => setBulkMode(false)}
                className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${!bulkMode ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                {t('singleTableMode')}
              </button>
              <button onClick={() => setBulkMode(true)}
                className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${bulkMode ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                {t('bulkAddMode')}
              </button>
            </div>

            {!bulkMode ? (
              /* ── Single table form ── */
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('tableNameLabel')}</label>
                  <input value={newTable.name} onChange={e => setNewTable({ ...newTable, name: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && handleAddTable()}
                    className="w-full border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base"
                    placeholder={t('tablesTableNamePlaceholder')} autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('capacityPeople')}</label>
                  <div className="flex gap-2 flex-wrap">
                    {[2, 4, 6, 8, 10, 12].map(n => (
                      <button key={n} onClick={() => setNewTable({ ...newTable, capacity: n })}
                        className={`w-14 h-14 rounded-xl font-bold text-base transition-all active:scale-95 ${
                          newTable.capacity === n ? 'bg-blue-600 text-white border-2 border-blue-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-700'
                        }`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                {tableCategories.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('categoryFloorZone')}</label>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setNewTable({ ...newTable, table_category_id: '' })}
                        className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                          !newTable.table_category_id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-700 text-gray-500'
                        }`}>
                        {t('uncategorized')}
                      </button>
                      {tableCategories.map(cat => (
                        <button key={cat.id} onClick={() => setNewTable({ ...newTable, table_category_id: cat.id })}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                            newTable.table_category_id === cat.id ? 'text-white border-transparent' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                          style={newTable.table_category_id === cat.id ? { backgroundColor: cat.color, borderColor: cat.color } : {}}>
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <BigButton onClick={handleAddTable} color="blue" disabled={submitting}>
                  {submitting ? `⏳ ${t('adding')}` : `+ ${t('addTableBtn')}`}
                </BigButton>
              </>
            ) : (
              /* ── Bulk table form ── */
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('prefixLabel')}</label>
                    <input value={bulkTable.prefix} onChange={e => setBulkTable({ ...bulkTable, prefix: e.target.value })}
                      className="w-full border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base"
                      placeholder={t('tableNo')} autoFocus />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('countLabel')} <span className="text-gray-400 font-normal">({t('maxLabel')} 50)</span></label>
                    <input type="number" inputMode="numeric" min="1" max="50" value={bulkTable.count}
                      onChange={e => setBulkTable({ ...bulkTable, count: e.target.value })}
                      className="w-full border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('startNumber')}</label>
                  <input type="number" inputMode="numeric" min="1" value={bulkTable.startFrom}
                    onChange={e => setBulkTable({ ...bulkTable, startFrom: e.target.value })}
                    className="w-full border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base" />
                </div>
                {/* Preview */}
                {bulkTable.prefix.trim() && parseInt(bulkTable.count) > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1.5">{t('tablesToCreate')} ({Math.min(parseInt(bulkTable.count) || 0, 50)} {t('countSuffix')}):</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400 leading-relaxed">
                      {Array.from({ length: Math.min(parseInt(bulkTable.count) || 0, 5) }, (_, i) =>
                        `${bulkTable.prefix.trim()} ${parseInt(bulkTable.startFrom) + i}`
                      ).join(', ')}
                      {parseInt(bulkTable.count) > 5 && `, ... ${bulkTable.prefix.trim()} ${parseInt(bulkTable.startFrom) + parseInt(bulkTable.count) - 1}`}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('capacityPeople')}</label>
                  <div className="flex gap-2 flex-wrap">
                    {[2, 4, 6, 8, 10, 12].map(n => (
                      <button key={n} onClick={() => setBulkTable({ ...bulkTable, capacity: n })}
                        className={`w-14 h-14 rounded-xl font-bold text-base transition-all active:scale-95 ${
                          bulkTable.capacity === n ? 'bg-blue-600 text-white border-2 border-blue-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-700'
                        }`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                {tableCategories.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('categoryLabel')} <span className="text-gray-400 font-normal">({t('optional').toLowerCase()})</span></label>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setBulkTable({ ...bulkTable, table_category_id: '' })}
                        className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                          !bulkTable.table_category_id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-700 text-gray-500'
                        }`}>
                        {t('uncategorized')}
                      </button>
                      {tableCategories.map(cat => (
                        <button key={cat.id} onClick={() => setBulkTable({ ...bulkTable, table_category_id: cat.id })}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                            bulkTable.table_category_id === cat.id ? 'text-white border-transparent' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                          style={bulkTable.table_category_id === cat.id ? { backgroundColor: cat.color, borderColor: cat.color } : {}}>
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <BigButton onClick={handleBulkAddTables} color="blue" disabled={submitting}>
                  {submitting ? `⏳ ${t('adding')}` : `+ ${Math.min(parseInt(bulkTable.count) || 0, 50)} ${t('addTableBtn')}`}
                </BigButton>
              </>
            )}
          </div>
        </BottomSheet>
      )}

      {/* ── Sheet: Manage Categories ── */}
      {sheet === 'manage-categories' && (
        <BottomSheet title={t('tablesManageCategoriesTitle')} onClose={() => setSheet(null)}>
          <div className="space-y-4">
            {/* Existing categories */}
            {tableCategories.length > 0 && (
              <div className="space-y-2">
                {tableCategories.map(cat => (
                  editCat?.id === cat.id ? (
                    /* Inline edit form */
                    <div key={cat.id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border-2 border-blue-300 dark:border-blue-700">
                      <input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2 focus:outline-none focus:border-blue-400" />
                      <div className="flex flex-wrap gap-2 md:gap-1.5 mb-3">
                        {CAT_COLORS.map(c => (
                          <button key={c} onClick={() => setCatForm(f => ({ ...f, color: c }))}
                            aria-label={`${t('tablesPickColorAria') || 'Renk seç'}: ${c}`}
                            aria-pressed={catForm.color === c}
                            className="w-10 h-10 md:w-7 md:h-7 rounded-full border-2 transition-all active:scale-90 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                            style={{ backgroundColor: c, borderColor: catForm.color === c ? '#1d4ed8' : 'transparent', boxShadow: catForm.color === c ? '0 0 0 2px white, 0 0 0 4px '+c : 'none' }} />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setEditCat(null); setCatForm({ name: '', color: '#6b7280' }); }}
                          className="flex-1 py-3 md:py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 active:scale-[0.97] transition-all">{t('cancel')}</button>
                        <button onClick={handleSaveCat} disabled={catSaving}
                          className="flex-1 py-3 md:py-2 rounded-xl text-sm bg-blue-600 text-white font-semibold disabled:opacity-50 active:scale-[0.97] transition-all">
                          {catSaving ? '...' : t('save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div key={cat.id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                      <div className="w-8 h-8 rounded-xl flex-shrink-0" style={{ backgroundColor: cat.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{cat.name}</div>
                        <div className="text-xs text-gray-400">{cat.table_count} {t('tablesTableLower')}</div>
                      </div>
                      <button onClick={() => { setEditCat(cat); setCatForm({ name: cat.name, color: cat.color }); }}
                        className="text-xs px-3 py-2.5 md:py-1.5 min-h-[40px] md:min-h-0 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 font-medium active:scale-[0.97] transition-all">{t('edit')}</button>
                      <button onClick={() => handleDeleteCat(cat)}
                        className="text-xs px-3 py-2.5 md:py-1.5 min-h-[40px] md:min-h-0 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-medium active:scale-[0.97] transition-all">{t('delete')}</button>
                    </div>
                  )
                ))}
              </div>
            )}

            {/* Add new category form */}
            {!editCat && (
              <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('tablesAddNewCategory')}</p>
                <input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={t('tablesCategoryPlaceholder')}
                  className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-400" />
                <div>
                  <p className="text-xs text-gray-500 mb-2">{t('tablesChooseColor')}</p>
                  <div className="flex flex-wrap gap-2">
                    {CAT_COLORS.map(c => (
                      <button key={c} onClick={() => setCatForm(f => ({ ...f, color: c }))}
                        aria-label={`${t('tablesPickColorAria') || 'Renk seç'}: ${c}`}
                        aria-pressed={catForm.color === c}
                        className="w-11 h-11 md:w-8 md:h-8 rounded-full border-2 transition-all active:scale-90 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                        style={{ backgroundColor: c, borderColor: catForm.color === c ? '#1d4ed8' : 'transparent', boxShadow: catForm.color === c ? '0 0 0 2px white, 0 0 0 4px '+c : 'none' }} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">{t('tablesPreviewLabel')}:</span>
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-semibold" style={{ backgroundColor: catForm.color }}>
                    <span className="w-2 h-2 rounded-full bg-white/60" />
                    {catForm.name || t('categoryNameLabel')}
                  </span>
                </div>
                <BigButton onClick={handleSaveCat} color="blue" disabled={catSaving || !catForm.name.trim()}>
                  {catSaving ? `⏳ ${t('saving')}` : `+ ${t('tablesAddCategoryBtn')}`}
                </BigButton>
              </div>
            )}
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
